import { useState } from "react";
import api, { formatErr, errCode, errDetails } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FloppyDisk } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Field component defined OUTSIDE parent to avoid remount / cursor-loss on every keystroke
function Field({ label, testid, error, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <input
        {...props}
        data-testid={testid}
        aria-invalid={!!error}
        className={`w-full mt-1.5 h-11 px-3 rounded-md border bg-card text-sm focus:outline-none focus:ring-2 ${
          error ? "border-signal focus:ring-signal/40" : "border-border focus:ring-signal/40"
        }`}
      />
      {error && <span className="text-xs text-signal mt-1 block" data-testid={`${testid}-error`}>{error}</span>}
    </label>
  );
}

const INVOICE_NO_RE = /^\d+$/;

const initial = {
  voucher_type: "Sales",
  invoice_no: "",
  date: new Date().toISOString().slice(0, 10),
  party_name: "",
  party_mobile: "",
  invoice_amount: "",
  salesman: "",
  transporter: "",
};

export default function AddSale() {
  const nav = useNavigate();
  const [voucherType, setVoucherType] = useState(initial.voucher_type);
  const [invoiceNo, setInvoiceNo] = useState(initial.invoice_no);
  const [date, setDate] = useState(initial.date);
  const [partyName, setPartyName] = useState(initial.party_name);
  const [partyMobile, setPartyMobile] = useState(initial.party_mobile);
  const [invoiceAmount, setInvoiceAmount] = useState(initial.invoice_amount);
  const [salesman, setSalesman] = useState(initial.salesman);
  const [transporter, setTransporter] = useState(initial.transporter);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // { invoice_no, existing }

  const payload = () => ({
    voucher_type: voucherType.trim(),
    invoice_no: invoiceNo.trim(),
    date,
    party_name: partyName.trim(),
    party_mobile: partyMobile.trim(),
    invoice_amount: Number(invoiceAmount),
    salesman: salesman.trim() || null,
    transporter: transporter.trim() || null,
  });

  const validate = () => {
    const e = {};
    if (!voucherType.trim()) e.voucher_type = "Voucher type is required";
    if (!invoiceNo.trim()) {
      e.invoice_no = "Invoice number is required";
    } else if (!INVOICE_NO_RE.test(invoiceNo.trim())) {
      e.invoice_no = "Invalid invoice number. It must be a plain number, e.g. 31, 42, etc.";
    }
    if (!partyName.trim()) e.party_name = "Party name is required";
    if (!/^\d{10,}$/.test(partyMobile.replace(/\D/g, ""))) e.party_mobile = "Mobile must be at least 10 digits";
    if (!(Number(invoiceAmount) > 0)) e.invoice_amount = "Invoice amount must be greater than 0";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; // prevent double-submit
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/sales", payload());
      toast.success("Invoice added successfully");
      nav("/");
    } catch (err) {
      if (err?.response?.status === 409 && errCode(err) === "DUPLICATE_INVOICE") {
        const d = errDetails(err);
        setDuplicate({ invoice_no: d.invoice_no || invoiceNo, existing: d.existing });
      } else if (err?.response?.status === 422) {
        const fieldErrors = errDetails(err)?.errors || [];
        const invField = fieldErrors.find((f) => f.field === "invoice_no");
        if (invField) {
          setErrors((prev) => ({ ...prev, invoice_no: invField.message }));
        } else {
          toast.error(formatErr(err));
        }
      } else {
        toast.error(formatErr(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmReplace = async () => {
    if (!duplicate) return;
    setBusy(true);
    try {
      await api.post(`/sales/${encodeURIComponent(duplicate.invoice_no)}/replace`, payload());
      toast.success(`Old invoice deleted. New invoice ${duplicate.invoice_no} created.`);
      setDuplicate(null);
      nav("/");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="add-sale-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Add New Sale</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manually create an invoice. Invoice number must be a plain number, e.g. <span className="font-mono">31</span>.
            Delivery status will default to <span className="font-mono">No</span>.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="brut-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="add-sale-form" noValidate>
        <div className="md:col-span-2">
          <Field label="Voucher Type" value={voucherType} onChange={(e) => setVoucherType(e.target.value)} testid="voucher-type-input" error={errors.voucher_type} />
        </div>
        <Field label="Invoice No" placeholder="31" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} testid="invoice-no-input" error={errors.invoice_no} />
        <Field label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} testid="date-input" />
        <Field label="Party Name" value={partyName} onChange={(e) => setPartyName(e.target.value)} testid="party-name-input" error={errors.party_name} />
        <Field label="Party Mobile" value={partyMobile} onChange={(e) => setPartyMobile(e.target.value)} testid="party-mobile-input" error={errors.party_mobile} />
        <Field label="Salesman" placeholder="e.g. Rajesh Kumar" value={salesman} onChange={(e) => setSalesman(e.target.value)} testid="salesman-input" error={errors.salesman} />
        <Field label="Transporter" placeholder="e.g. Shree Cargo Movers" value={transporter} onChange={(e) => setTransporter(e.target.value)} testid="transporter-input" error={errors.transporter} />
        <div className="md:col-span-2">
          <Field label="Invoice Amount (₹)" type="number" min="0" step="0.01" value={invoiceAmount} onChange={(e) => setInvoiceAmount(e.target.value)} testid="invoice-amount-input" error={errors.invoice_amount} />
        </div>
        <div className="md:col-span-2 flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => nav("/")} className="h-10 px-4 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button type="submit" disabled={busy} data-testid="save-sale-button" className="signal-btn h-10 px-5 rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            <FloppyDisk size={16} weight="bold" /> {busy ? "Saving..." : "Save Invoice"}
          </button>
        </div>
      </form>

      <AlertDialog open={!!duplicate} onOpenChange={(o) => !o && setDuplicate(null)}>
        <AlertDialogContent data-testid="duplicate-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Invoice No already exists</AlertDialogTitle>
            <AlertDialogDescription>
              An invoice with the number <strong className="font-mono">{duplicate?.invoice_no}</strong> already exists
              {duplicate?.existing ? (
                <> — currently for <strong>{duplicate.existing.party_name}</strong>, ₹{Number(duplicate.existing.invoice_amount || 0).toLocaleString("en-IN")}.</>
              ) : "."}
              <br /><br />
              Do you want to <strong>delete the old transaction</strong> and create this new one in its place? This action is logged to the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReplace} disabled={busy} data-testid="duplicate-replace-button" className="signal-btn">
              {busy ? "Replacing..." : "Delete & Create New"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
